export function renderBuildSeedScriptEnvFileTemplate({
  authBasePath
}: {
  authBasePath: string
}): string {
  return `/**
 * AUTO-GENERATED by BetterStart — safe to delete after running.
 */

import { readFileSync } from 'node:fs'
import { resolve } from 'node:path'

// Parsed by hand to avoid a @next/env dependency in the generated project.
for (const envFile of ['.env.local', '.env']) {
  try {
    const content = readFileSync(resolve(process.cwd(), envFile), 'utf-8')
    for (const line of content.split('\\n')) {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#')) continue
      const eqIdx = trimmed.indexOf('=')
      if (eqIdx === -1) continue
      const key = trimmed.slice(0, eqIdx).trim()
      const val = trimmed.slice(eqIdx + 1).trim().replace(/^['"]|['"]$/g, '')
      if (!process.env[key]) process.env[key] = val
    }
  } catch {}
}

import { drizzle } from 'drizzle-orm/postgres-js'
import { eq } from 'drizzle-orm'
import postgres from 'postgres'
import * as schema from '../lib/db/schema'
import { betterAuth } from 'better-auth'
import { drizzleAdapter } from 'better-auth/adapters/drizzle'

// Inline DB connection (mirrors generated lib/db/client.ts)
const databaseUrl = process.env.DATABASE_URL
if (!databaseUrl) {
  throw new Error('DATABASE_URL is required')
}

const client = postgres(databaseUrl)
const db = drizzle({ client, schema })

// Inline auth setup (mirrors generated lib/actions/auth/server.ts)
const auth = betterAuth({
  secret: process.env.BETTERSTART_AUTH_SECRET,
  baseURL: process.env.BETTERSTART_AUTH_URL,
  basePath: process.env.BETTERSTART_AUTH_BASE_PATH || '${authBasePath}',
  database: drizzleAdapter(db, {
    provider: 'pg',
    schema: {
      user: schema.user,
      session: schema.session,
      account: schema.account,
      verification: schema.verification,
    },
  }),
  emailAndPassword: { enabled: true, minPasswordLength: 8 },
  user: {
    additionalFields: {
      role: { type: 'string', required: false, defaultValue: 'member', input: false },
    },
  },
})

const CHECK_ADMIN_ONLY = process.env.SEED_CHECK_ADMIN === 'true'
const OVERWRITE_MODE = process.env.SEED_OVERWRITE_MODE

async function clearAuthorshipReferences(userId: string) {
  for (const table of Object.values(schema)) {
    if (!table || typeof table !== 'object') continue
    const tableRef = table as unknown as Record<string, unknown>

    if (tableRef.createdBy) {
      await db
        .update(table as never)
        .set({ createdBy: null })
        .where(eq(tableRef.createdBy as never, userId))
    }

    if (tableRef.updatedBy) {
      await db
        .update(table as never)
        .set({ updatedBy: null })
        .where(eq(tableRef.updatedBy as never, userId))
    }
  }
}

async function main() {
  const existingAdmin = await db
    .select({ id: schema.user.id, email: schema.user.email, name: schema.user.name })
    .from(schema.user)
    .where(eq(schema.user.role, 'admin'))
    .then((rows: { id: string; email: string; name: string }[]) => rows[0])

  if (CHECK_ADMIN_ONLY) {
    if (existingAdmin) {
      console.log(\`EXISTING_ADMIN:\${JSON.stringify(existingAdmin)}\`)
      process.exit(3)
    }
    process.exit(0)
  }

  const EMAIL = process.env.SEED_EMAIL
  const PASSWORD = process.env.SEED_PASSWORD
  const NAME = process.env.SEED_NAME || 'Admin'

  if (!EMAIL || !PASSWORD) {
    console.error('  SEED_EMAIL and SEED_PASSWORD are required.')
    process.exit(1)
  }

  const existingUser = await db
    .select({ id: schema.user.id, name: schema.user.name })
    .from(schema.user)
    .where(eq(schema.user.email, EMAIL))
    .then((rows: { id: string; name: string }[]) => rows[0])

  const replaceTarget =
    OVERWRITE_MODE === 'admin' ? existingAdmin : OVERWRITE_MODE === 'email' ? existingUser : undefined

  if (existingUser && !replaceTarget) {
    // Exit code 2 signals "user exists" to the CLI
    console.log(\`EXISTING_USER:\${existingUser.name}\`)
    process.exit(2)
  }

  if (replaceTarget) {
    console.log(
      OVERWRITE_MODE === 'admin'
        ? '\\n  Replacing existing admin user...'
        : '\\n  Replacing existing account...'
    )
    await clearAuthorshipReferences(replaceTarget.id)
    // Remove existing account + session rows first (foreign key refs)
    await db.delete(schema.session).where(eq(schema.session.userId, replaceTarget.id))
    await db.delete(schema.account).where(eq(schema.account.userId, replaceTarget.id))
    await db.delete(schema.user).where(eq(schema.user.id, replaceTarget.id))
  } else {
    console.log('\\n  Creating admin user...')
  }

  console.log(\`  Email: \${EMAIL}\\n\`)

  const result = await auth.api.signUpEmail({
    body: { email: EMAIL, password: PASSWORD, name: NAME },
  })

  if (!result?.user) {
    console.error('  Failed to create user.')
    process.exit(1)
  }

  await db
    .update(schema.user)
    .set({ role: 'admin' })
    .where(eq(schema.user.id, result.user.id))

  console.log(\`  Admin user \${replaceTarget ? 'replaced' : 'created'}: \${EMAIL}\`)
  console.log('  Role: admin\\n')
  process.exit(0)
}

main().catch((err) => {
  console.error('  Seed failed:', err.message || err)
  process.exit(1)
})
`
}
